<template>
  <v-main>
    <v-container fluid>
      <v-card>
        <v-toolbar flat class="card-bar">
          <v-toolbar-title>CAN Sniffer</v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pa-3">
          <ORow>
            <div class="oderon-col-6 pa-3">
              <v-textarea
                class="monospace-input"
                v-model="rawdata"
                label="Rawdata (hex)"
              />
            </div>
            <div class="oderon-col-3 pa-3">
              <v-textarea
                class="monospace-input"
                v-model="cardOrder"
                label="Ordem dos crachás"
              />
            </div>
            <div class="oderon-col-3 pa-3">
              <v-textarea
                class="monospace-input"
                v-model="plotConfigs"
                label="Plot Configs (pgn start end)"
              />
              <v-btn @click="generatePlots">
                Gerar Gráficos
              </v-btn>
            </div>
          </ORow>
        </v-card-text>
        <v-card-actions class="pa-6">
          <span class="mr-4" style="max-width: 200px">
            <v-text-field
              label="Firmware Version (hex)"
              prefix="0x"
              v-model="fwVersion"
            />
          </span>
          <span class="mr-4" style="max-width: 300px">
            <v-switch
              label="Apenas registros com passagem de crachá"
              v-model="onlyWithCard"
            />
          </span>
          <v-btn color="primary" large @click="parse">Parse</v-btn>

          <div>Parseado em {{ parseDeltaMs }} ms</div>
          <v-spacer />
          <span class="mr-4">
            <v-text-field
              label="Min timestamp"
              hint="Y-M-D H:m:s (ou copie da tabela abaixo com ';')"
              v-model="minTimestamp"
            />
          </span>
          <span class="mr-4">
            <v-text-field
              label="Max timestamp"
              hint="Y-M-D H:m:s (ou copie da tabela abaixo com ';')"
              v-model="maxTimestamp"
            />
          </span>
        </v-card-actions>
      </v-card>

      <SniffDisplay class="mt-4" v-if="rows != null" :rows="rows" />

      <v-card v-if="rows != null">
        <v-toolbar flat class="card-bar">
          <v-toolbar-title>Plots</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <OCol>
            <Plot v-for="c in configs" :rows="rows" :config="c" :key="c.key" />
          </OCol>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import Slicer from 'horus-parser/src/Slicer'
import brush from 'brush'
import SniffDisplay from './SniffDisplay.vue'
import Plot from './Plot.vue'
import moment from 'moment'
import PlotConfig from './PlotConfig'
import { selectOperations } from 'horus-parser/src/tools/select-operations.mjs'
import utils from 'oderon-commons/src/utils.js'

function store(key, value) {
  if (value == null || value.trim().length < 1) {
    localStorage.removeItem(key)
  } else {
    localStorage.setItem(key, value)
  }
}

function maybeDt(x) {
  if (x == null || x.trim().length < 1) return null
  let m = x.match(/^(\d{4}-\d{2}-\d{2});(\d{2}:\d{2}:\d{2})/)
  if (m) {
    x = m[1] + 'T' + m[2]
  }

  return moment(x)
}

export default {
  components: { SniffDisplay, Plot },
  data() {
    return {
      rawdata: localStorage.getItem('CanSniff_rawdata'),
      cardOrder: localStorage.getItem('CanSniff_cardOrder'),
      rows: null,
      fwVersion: '02',
      newAsciiFormat: true,
      onlyWithCard: false,
      minTimestamp: null,
      maxTimestamp: null,
      plotConfigs: localStorage.getItem('CanSniff_plotConfigs'),
      configs: [],
      parseDeltaMs: null
    }
  },

  watch: {
    rawdata() {
      store('CanSniff_rawdata', this.rawdata)
    },
    cardOrder() {
      store('CanSniff_cardOrder', this.cardOrder)
    },
    plotConfigs() {
      store('CanSniff_plotConfigs', this.plotConfigs)
    }
  },

  computed: {
    cardsInOrder() {
      let s = this.cardOrder && this.cardOrder.trim()
      if (s == null || s.length < 1) return []
      return s.split('\n')
    }
  },

  methods: {
    parse() {
      let d0 = +Date.now()
      let bytes = brush.hexToBytes(this.rawdata),
        slicer = new Slicer(bytes, {
          startPointer: 0,
          keepBytesInLapses: false,
          keepBytesInFrames: false,
          newAsciiFormat: this.newAsciiFormat,
          lastFwVersion: parseInt(this.fwVersion, 16),
          supportCanSniff: true
        })

      let data = slicer.slice()
      // console.log('sliced data is', data)

      // Guarda apenas os frames que são do tipo sniff,
      // e dentre esses apenas os que tem crachá (se solicitado)

      let minTs = maybeDt(this.minTimestamp)
      let maxTs = maybeDt(this.maxTimestamp)

      let operations = selectOperations(data.frames)
      let rows = operations.filter((x) => {
        if (x.typeStr != 'generic') return false
        if (x.subTypeStr != 'sniff') return false
      })

      // Ainda é possível que existam frames inteiros do tipo
      // 88
      for (let frame of data.frames) {
        if (frame.type == 'sniff') rows.push(frame)
      }

      // let rows = data.frames.filter((x) => {
      //   if (x.type != 'sniff') return false
      //   if (this.onlyWithCard && !x.hasCard) return false
      //   if (minTs && minTs.isAfter(x.timestamp)) return false
      //   if (maxTs && maxTs.isBefore(x.timestamp)) return false
      //   return true
      // })

      rows = rows.filter((x) => {
        if (this.onlyWithCard && !x.hasCard) return false
        if (minTs && minTs.isAfter(x.timestamp)) return false
        if (maxTs && maxTs.isBefore(x.timestamp)) return false
        return true
      })

      rows = utils.orderBy(rows, [['timestamp', 'asc']])

      // Para cada linha, verifica se consegue especificar o label
      // do crachá. Além disso, converte o hex da linha em um BigInt.
      let clone = [...this.cardsInOrder]
      for (let row of rows) {
        if (row.hasCard) row.cardLabel = clone.shift() || 'card'
        if (row.sample) {
          for (let variable of row.sample) {
            // eslint-disable-next-line no-undef
            variable.bigValue = BigInt('0x' + variable.hexValue)
          }
        }
      }

      this.rows = rows
      let d1 = +Date.now()

      this.parseDeltaMs = d1 - d0
    },

    generatePlots() {
      let s = this.plotConfigs && this.plotConfigs.trim()

      if (s == null || s.length < 1) {
        return null
      }

      this.configs = s.split('\n').map((line) => new PlotConfig(line))
    }
  }
}
</script>

<style></style>
